import {
  DeepPartial,
  isBaseContainers,
  IUICfgAttributesContainerElementOptions,
  IUICfgComponentContainerElementOptions,
  IUiCfgContainer,
  IUICfgRootContainer,
  UICfgAttributesContainerElementOptionsDefault,
  UICfgComponentContainerElementOptionsDefault,
  UICfgContainerType,
  UICfgBooleanAttributeElementOptionsDefault,
  UICfgDecimalAttributeElementOptionsDefault,
  UICfgIntegerAttributeElementOptionsDefault,
  UICfgRalAttributeElementOptionsDefault,
  UICfgTextAttributeElementOptionsDefault,
  mergeUIConfigSettings,
} from '@canvas-logic/clp-api-types';

/*
The user-defined container contains mixed types of items,
but during rendering, each type of item should have its own container
to render correctly and process some merge options.
To achieve this, the layout configuration should be processed
and additional virtual containers created when necessary.
  // TODO: for optimisation purpose should be moved to the api
*/

/** Maximum container nesting level for styling purposes */
const MAX_CONTAINER_LEVEL = 3;

export const prepareUILayout = (rootContainer: IUICfgRootContainer) => {
  const prepareNext = <T extends IUiCfgContainer>(
    container: T,
    {
      componentsSettings,
      attributesSettings,
    }: {
      componentsSettings?: DeepPartial<IUICfgComponentContainerElementOptions>;
      attributesSettings?: DeepPartial<IUICfgAttributesContainerElementOptions>;
    },
    level: number = 1,
    parentLevel?: number,
  ): IUiCfgContainer => {
    const children: Array<(typeof container)['children']> = [];

    // Check if parent container has combineIntoGrid enabled
    const isGridMode = 'combineIntoGrid' in container && container.combineIntoGrid === true;

    // Mirror admin UI's UIRootContainer / UIMixedContainer filter (see clp-admin-ui):
    // drop items with notAvailable, and ComponentItems containers whose component child
    // is notAvailable — this catches cases where the container itself wasn't flagged
    // (e.g. UI config regenerated after the component-on-product was disabled).
    for (const child of container.children.filter(
      (it) =>
        it.enabled &&
        !it.notAvailable &&
        !(
          it.kind === 'container' &&
          it.type === UICfgContainerType.ComponentItems &&
          (it.children as any)?.[0]?.notAvailable
        ),
    )) {
      if (child.kind === 'container') {
        if (isBaseContainers(child)) {
          // Child containers always get parent level + 1, capped at MAX_CONTAINER_LEVEL
          const nextLevel = Math.min(level + 1, MAX_CONTAINER_LEVEL);

          if (child.type !== UICfgContainerType.ProductMerge) {
            const processedContainer = prepareNext(
              child,
              {
                componentsSettings: mergeUIConfigSettings(
                  child.componentsSettings,
                  componentsSettings,
                  UICfgComponentContainerElementOptionsDefault,
                ),
                attributesSettings: mergeUIConfigSettings(
                  child.attributesSettings,
                  attributesSettings,
                  UICfgAttributesContainerElementOptionsDefault,
                ),
              },
              nextLevel,
              level, // pass current container's level as parentLevel for the child
            );
            // Drop ComponentItems containers whose component + all attributes were filtered out —
            // otherwise an empty wrapper renders and adds gap-section between siblings.
            if (
              processedContainer.type === UICfgContainerType.ComponentItems &&
              processedContainer.children.length === 0
            ) {
              continue;
            }
            // Set _gridMode flag on child if parent has combineIntoGrid enabled
            if (isGridMode) {
              (processedContainer as any)._gridMode = true;
            }
            children.push(processedContainer as any);
          } else {
            const processedContainer = prepareNext(
              child,
              {
                componentsSettings: mergeUIConfigSettings(
                  child.productsSettings,
                  componentsSettings,
                  UICfgComponentContainerElementOptionsDefault,
                ),
              },
              nextLevel,
              level, // pass current container's level as parentLevel for the child
            );
            // Set _gridMode flag on child if parent has combineIntoGrid enabled
            if (isGridMode) {
              (processedContainer as any)._gridMode = true;
            }
            children.push(processedContainer as any);
          }
        }
      } else {
        if (child.kind === 'attribute') {
          let defaultAttributeSettings = UICfgAttributesContainerElementOptionsDefault;
          switch (child.type) {
            case 'RAL':
              defaultAttributeSettings = UICfgRalAttributeElementOptionsDefault;
              break;
            case 'text':
              defaultAttributeSettings = UICfgTextAttributeElementOptionsDefault;
              break;
            case 'integer':
              defaultAttributeSettings = UICfgIntegerAttributeElementOptionsDefault;
              break;
            case 'boolean':
              defaultAttributeSettings = UICfgBooleanAttributeElementOptionsDefault;
              break;
            case 'decimal':
              defaultAttributeSettings = UICfgDecimalAttributeElementOptionsDefault;
              break;
          }

          child.uiControl = mergeUIConfigSettings(child.uiControl, attributesSettings, defaultAttributeSettings);
        } else if (child.kind === 'component') {
          child.uiControl = mergeUIConfigSettings(
            child.uiControl,
            componentsSettings,
            UICfgComponentContainerElementOptionsDefault,
          );
        }
        // Set _parentLevel for leaf items (components, attributes) to inherit container level
        (child as any)._parentLevel = level;
        // Set _gridMode flag on child if parent has combineIntoGrid enabled
        if (isGridMode) {
          (child as any)._gridMode = true;
        }
        children.push(child as any);
      }
    }

    // `_level`/`_parentLevel` are virtual fields this builder stamps on for title styling
    // and are absent from the config DTOs — the same reason children get them via
    // `(child as any)._parentLevel` above. The assertion suppresses the excess-property
    // check on the literal without altering what is returned.
    return {
      ...container,
      _level: level,
      _parentLevel: parentLevel, // level of the parent container (for title styling)
      children: children as any,
    } as unknown as IUiCfgContainer;
  };
  const { attributesSettings, componentsSettings } = rootContainer;

  const processedLayout = prepareNext(
    rootContainer,
    {
      attributesSettings: mergeUIConfigSettings(
        attributesSettings,
        undefined,
        UICfgAttributesContainerElementOptionsDefault,
      ),
      componentsSettings: mergeUIConfigSettings(
        componentsSettings,
        undefined,
        UICfgComponentContainerElementOptionsDefault,
      ),
    },
    1, // Root container starts at level 1
  );
  return processedLayout;
};
